import type { Donnee } from './types'
import * as f from './format'
import { PRECISIONS_DATE, STATUTS_TRAITEMENT, TYPES_ANTECEDENT } from '@doc/shared'

/**
 * « depuis le 14 mars 2026 », mais « depuis 2020 » quand seule l'année est
 * connue : on n'ajoute pas un jour que le document ne donnait pas.
 */
function depuis(mot: string, valeur: unknown, precision: unknown): string | null {
  if (!valeur) return null
  const p = (precision as keyof typeof PRECISIONS_DATE) ?? 'JOUR'
  const texte = f.dateSelonPrecision(valeur as string, p)
  return p === 'JOUR' ? `${mot} le ${texte}` : `${mot} ${texte}`
}

/**
 * Le résumé affiché sous le libellé, propre à chaque catégorie.
 * `sansType` sert quand la nature de l'antécédent est déjà le titre du groupe :
 * inutile de la répéter sur chaque ligne.
 */
export function resume(donnee: Donnee, options?: { sansType?: boolean }): string {
  const d = (donnee.detail ?? {}) as Record<string, unknown>
  const morceaux: (string | null | undefined)[] = []
  const dateAvecPrecision = (valeur: unknown, precision: unknown) =>
    valeur
      ? f.dateSelonPrecision(
          valeur as string,
          (precision as keyof typeof PRECISIONS_DATE) ?? 'JOUR',
        )
      : null

  switch (donnee.categorie) {
    case 'ANTECEDENT':
      morceaux.push(
        options?.sansType
          ? null
          : TYPES_ANTECEDENT[d.type as keyof typeof TYPES_ANTECEDENT],
        d.cote as string,
        dateAvecPrecision(d.date, d.precisionDate),
        d.precisions as string,
      )
      break
    case 'ALLERGIE':
      morceaux.push(d.reaction as string, d.gravite as string, d.precisions as string)
      break
    case 'DIAGNOSTIC':
      // Le code et les précisions restent dans la fiche : sur la carte, un
      // diagnostic doit se lire d'un coup d'œil.
      morceaux.push(
        dateAvecPrecision(d.dateDiagnostic, d.precisionDate),
        d.actif === false ? 'inactif' : null,
      )
      break
    case 'TRAITEMENT':
      // L'indication reste dans la fiche : sur la ligne, on veut ce que la
      // patiente prend, pas pourquoi.
      morceaux.push(
        [d.dosage, d.posologie, d.voie].filter(Boolean).join(' · ') || null,
        depuis('depuis', d.dateDebut, d.precisionDate),
        depuis('arrêté', d.dateFin, 'JOUR'),
      )
      break
    case 'EVENEMENT_HISTOIRE':
      morceaux.push(
        dateAvecPrecision(d.date, d.precisionDate),
        d.categorie as string,
        d.description as string,
      )
      break
    case 'DONNEE_SPECIFIQUE':
      morceaux.push(d.valeur as string, d.date ? f.dateCourte(d.date as string) : null)
      break
    default:
      break
  }
  return morceaux.filter(Boolean).join(' · ')
}


// Vocabulaire des champs de détail, pour ne jamais afficher un nom technique.
const ETIQUETTES: Record<string, string> = {
  libelle: 'Libellé',
  substance: 'Substance',
  denomination: 'Traitement',
  type: 'Nature',
  date: 'Date',
  dateDiagnostic: 'Date du diagnostic',
  dateDebut: 'Début',
  dateFin: 'Fin',
  precisionDate: 'Précision de la date',
  cote: 'Côté',
  precisions: 'Précisions',
  reaction: 'Réaction',
  gravite: 'Gravité',
  code: 'Code',
  actif: 'Actif',
  dci: 'Dénomination commune',
  dosage: 'Dosage',
  posologie: 'Posologie',
  voie: 'Voie',
  indication: 'Indication',
  prescripteur: 'Prescripteur',
  description: 'Description',
  categorie: 'Catégorie',
  domaine: 'Domaine',
  valeur: 'Valeur',
  statut: 'Statut',
}

const DATES = new Set(['date', 'dateDiagnostic', 'dateDebut', 'dateFin'])

/** Le détail d'une donnée, champ par champ, en français lisible. */
export function detailLisible(
  detail: Record<string, unknown> | null | undefined,
): { etiquette: string; valeur: string }[] {
  if (!detail) return []
  return Object.entries(detail)
    .filter(([cle, valeur]) => {
      if (cle === 'donneeId' || cle === 'precisionDate') return false
      return valeur !== null && valeur !== undefined && valeur !== ''
    })
    .map(([cle, valeur]) => {
      let affichage: string
      if (DATES.has(cle)) {
        affichage = f.dateSelonPrecision(
          valeur as string,
          (detail.precisionDate as keyof typeof PRECISIONS_DATE) ?? 'JOUR',
        )
      } else if (typeof valeur === 'boolean') {
        affichage = valeur ? 'oui' : 'non'
      } else if (cle === 'type') {
        affichage = TYPES_ANTECEDENT[valeur as keyof typeof TYPES_ANTECEDENT] ?? String(valeur)
      } else if (cle === 'statut') {
        affichage = STATUTS_TRAITEMENT[valeur as keyof typeof STATUTS_TRAITEMENT] ?? String(valeur)
      } else {
        affichage = String(valeur)
      }
      return { etiquette: ETIQUETTES[cle] ?? cle, valeur: affichage }
    })
}
